/**
 * 🖤 NYX & KURSON - Les Guides de l'Ombre
 * 
 * "Dans l'ombre, on ne ment plus..."
 * 
 * Nyx (🖤) - La gardienne mystérieuse, underground chic
 * Kurson (🔮) - L'oracle ténébreux, bad boy philosophe
 * 
 * Ils sont provocateurs, séduisants, et te poussent à assumer tes désirs.
 * Jamais vulgaires, toujours élégants. Dark mais classe.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Flame, Sparkles, X, ChevronRight, Eye, Heart } from 'lucide-react';
import { Button } from '../../ui/button';
import { GUIDE_IMAGES } from '../soul/GuideAvatar';

// 💬 Messages de Nyx (remplace Lilith)
const NYX_MESSAGES = {
  welcome: [
    "Bienvenue dans l'ombre... 🖤 Je suis Nyx. Ici, pas de masques. Que désires-tu vraiment ?",
    "Alors... tu oses franchir le seuil ? 🌙 Je t'attendais. Montre-moi qui tu es dans le noir...",
    "Les étoiles brillent plus fort dans l'obscurité... ✨ Je suis Nyx, et je vais révéler tes vrais désirs."
  ],
  sinReveal: [
    "Intéressant... Je sens ton énergie. 🔥 Laisse-moi deviner ce qui te consume...",
    "Chaque âme a son péché dominant... 🖤 Le tien est sur le point de se révéler...",
    "Ne résiste pas... Tes désirs ne mentent jamais. 🌙 Voyons ce que tu caches..."
  ],
  matchFound: [
    "Oh oh... 🔥 Quelqu'un vibre sur la même fréquence sombre que toi...",
    "L'ombre appelle l'ombre... 🖤 Tu as trouvé un(e) complice...",
    "Vos péchés s'attirent... 😈 Cette connexion promet d'être... intense."
  ],
  encouragement: [
    "La patience est une vertu... Mais ici, on préfère les péchés. 🖤 Continue de chasser...",
    "Les meilleures proies se méritent... 🌙 Ton moment viendra...",
    "L'ombre révèle les vrais visages... 🔥 Le bon pécheur croisera ta route."
  ],
  messageTip: [
    "Conseil de Nyx : Un message qui assume, ça change tout... 🖤 Sois direct(e).",
    "Les mots ont du pouvoir dans l'ombre... 🔥 Utilise-les pour séduire, pas pour supplier.",
    "Psst... Un compliment audacieux vaut mille 'salut ça va'. 😈 Ose."
  ],
  sinMatch: {
    luxuria: "Luxure contre Luxure... 🔥 Cette rencontre va être... mémorable.",
    superbia: "Deux égos qui se rencontrent... 👑 Que le meilleur gagne... ou que vous perdiez ensemble.",
    invidia: "L'envie mutuelle... 💚 Vous voulez ce que l'autre a. Prenez-le.",
    gula: "Gourmands tous les deux... 🍷 Il y a tant à goûter ensemble...",
    avaritia: "Deux collectionneurs... 💎 Serez-vous la prise ultime l'un pour l'autre ?",
    ira: "Passion contre passion... ⚡ Ça va être explosif.",
    acedia: "Deux paresseux... 🌙 Qui fera le premier pas ? Le suspense..."
  }
};

// 💬 Messages de Kurson (remplace Dante)
const KURSON_MESSAGES = {
  welcome: [
    "Te voilà dans mon domaine... 🔮 Je suis Kurson. Ici, on joue sans masques. Tu suis ?",
    "L'ombre te va bien... 🖤 Bienvenue. Je vais t'apprendre à chasser...",
    "Alors, prête à explorer ton côté sombre ? 🔥 Kurson, pour te servir... ou te tenter."
  ],
  sinReveal: [
    "Chaque âme cache un péché... 🔮 Laisse-moi lire le tien...",
    "Je sens quelque chose de puissant en toi... 🔥 Ton péché dominant se révèle...",
    "N'aie pas peur de ton ombre... 🖤 Elle est magnifique. Voyons ça..."
  ],
  matchFound: [
    "Bien joué... 🔮 Quelqu'un a mordu à l'hameçon... ou c'est toi qui as mordu ?",
    "Match dans l'ombre ! 🔥 Vos péchés se sont reconnus...",
    "La chasse porte ses fruits... 🖤 Cette connexion sent le soufre. J'adore."
  ],
  encouragement: [
    "La nuit est jeune... 🔮 Continue de rôder...",
    "Les meilleures rencontres arrivent quand on s'y attend le moins... 🌙 Patience, chasseuse.",
    "Ton péché attire les bonnes énergies... 🔥 Fais confiance à l'ombre."
  ],
  messageTip: [
    "Kurson te conseille : Un message mystérieux intrigue plus qu'un roman... 🔮 Sois énigmatique.",
    "L'art de la séduction, c'est le non-dit... 🖤 Laisse deviner, ne révèle pas tout.",
    "Un défi, une provocation... 🔥 Ça marche mieux que les compliments mièvres."
  ],
  sinMatch: {
    luxuria: "Deux heartbeatss qui se rencontrent... 🔥 Attention à l'incendie.",
    superbia: "L'orgueil aime l'orgueil... 👑 Vous allez vous admirer... ou vous affronter.",
    invidia: "L'envie attire l'envie... 💚 Vous voulez tous les deux ce que vous ne pouvez avoir. Intéressant.",
    gula: "Gourmandes de la vie... 🍷 Ensemble, vous allez tout dévorer.",
    avaritia: "Deux âmes qui collectionnent... 💎 Serez-vous mutuellement le trophée ultime ?",
    ira: "La colère rencontre la colère... ⚡ Dispute ou passion ? Même combat.",
    acedia: "La paresse a du charme... 🌙 Vous attendez que l'autre fasse le premier pas. Classique."
  }
};

// 🎯 Sélectionner le bon guide selon les préférences
export const getShadowGuideForUser = (gender, lookingFor) => {
  if (lookingFor === 'female' || (lookingFor === 'all' && gender === 'male')) {
    return 'nyx';
  } else if (lookingFor === 'male' || (lookingFor === 'all' && gender === 'female')) {
    return 'kurson';
  }
  return gender === 'female' ? 'kurson' : 'nyx';
};

// 📝 Récupérer un message aléatoire
const getRandomMessage = (guide, type, sin = null) => {
  const messages = guide === 'nyx' ? NYX_MESSAGES : KURSON_MESSAGES;
  
  if (type === 'sinMatch' && sin) {
    return messages.sinMatch[sin] || messages.matchFound[0];
  }
  
  const typeMessages = messages[type] || messages.welcome;
  return typeMessages[Math.floor(Math.random() * typeMessages.length)];
};

// 🎨 Composant principal du Guide Shadow avec GRAND VISAGE
export const ShadowGuide = ({ 
  guide = 'nyx', 
  messageType = 'welcome',
  customMessage = null,
  sin = null,
  onDismiss,
  showTip = false,
  position = 'bottom-right',
  bigFace = false  // Mode grand visage
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  
  const guideInfo = {
    nyx: {
      name: 'Nyx',
      emoji: '🖤',
      title: 'Gardienne de l\'Ombre',
      color: 'from-purple-900 to-black',
      accentColor: 'purple',
      bgGlow: 'shadow-purple-500/50',
      image: GUIDE_IMAGES.nyx?.confident_smirk || GUIDE_IMAGES.nyx?.default,
    },
    kurson: {
      name: 'Kurson',
      emoji: '🔮',
      title: 'Oracle de l\'Ombre',
      color: 'from-amber-900 to-black',
      accentColor: 'amber',
      bgGlow: 'shadow-amber-500/50',
      image: GUIDE_IMAGES.kurson?.knowing_smirk || GUIDE_IMAGES.kurson?.default,
    }
  };
  
  const info = guideInfo[guide] || guideInfo.nyx;
  
  useEffect(() => {
    if (messageType === 'sinMatch' && sin) {
      setCurrentMessage(customMessage || getRandomMessage(guide, 'sinMatch', sin));
    } else {
      setCurrentMessage(customMessage || getRandomMessage(guide, messageType));
    }
  }, [guide, messageType, customMessage, sin]);
  
  const positionClasses = {
    'bottom-right': 'fixed bottom-4 right-4',
    'bottom-left': 'fixed bottom-4 left-4',
    'center': 'fixed bottom-1/4 left-1/2 -translate-x-1/2'
  };
  
  if (!isVisible) return null;
  
  // Version minimisée
  if (isMinimized) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className={`${positionClasses[position]} z-50`}
        onClick={() => setIsMinimized(false)}
      >
        <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${info.color} p-0.5 shadow-lg ${info.bgGlow}`}>
          {info.image ? (
            <img src={info.image} alt={info.name} className="w-full h-full rounded-full object-cover" />
          ) : (
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
              <span className="text-2xl">{info.emoji}</span>
            </div>
          )}
        </div>
        <motion.div 
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full"
        />
      </motion.button>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.8 }}
      className={`${positionClasses[position]} z-50 max-w-sm`}
    >
      <div className={`
        bg-black/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-800
        overflow-hidden ${info.bgGlow}
      `}>
        {/* Header avec GRAND VISAGE */}
        <div className={`relative bg-gradient-to-r ${info.color} p-4`}>
          {/* Particules flottantes */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white/30 rounded-full"
                initial={{ 
                  x: `${20 + i * 20}%`, 
                  y: '100%',
                  opacity: 0 
                }}
                animate={{ 
                  y: '0%',
                  opacity: [0, 0.5, 0],
                }}
                transition={{ 
                  duration: 3 + i * 0.5, 
                  repeat: Infinity,
                  delay: i * 0.5,
                }}
              />
            ))}
          </div>
          
          <div className="relative flex items-center gap-3">
            {/* Grand visage du guide */}
            <motion.div 
              animate={{ 
                boxShadow: [
                  `0 0 20px ${info.accentColor === 'purple' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(245, 158, 11, 0.5)'}`,
                  `0 0 40px ${info.accentColor === 'purple' ? 'rgba(168, 85, 247, 0.8)' : 'rgba(245, 158, 11, 0.8)'}`,
                  `0 0 20px ${info.accentColor === 'purple' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(245, 158, 11, 0.5)'}`,
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-16 h-16 rounded-full overflow-hidden"
            >
              {info.image ? (
                <img 
                  src={info.image} 
                  alt={info.name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-black/50 flex items-center justify-center text-3xl">
                  {info.emoji}
                </div>
              )}
            </motion.div>
            <div className="flex-1">
              <h4 className="font-bold text-white flex items-center gap-2">
                {info.name}
                <Moon className="w-4 h-4 text-purple-300" />
              </h4>
              <p className="text-white/60 text-xs">{info.title}</p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 rounded-full hover:bg-white/10 text-white/60"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {onDismiss && (
                <button
                  onClick={() => { setIsVisible(false); onDismiss?.(); }}
                  className="p-1.5 rounded-full hover:bg-white/10 text-white/60"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Message */}
        <div className="p-4">
          <p className="text-gray-300 text-sm leading-relaxed">
            {currentMessage}
          </p>
          
          {/* Tip bonus */}
          {showTip && (
            <div className="mt-3 p-3 rounded-xl bg-purple-900/30 border border-purple-500/30">
              <div className="flex items-start gap-2">
                <Eye className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-400">
                  {getRandomMessage(guide, 'messageTip')}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Action */}
        <div className="px-4 pb-4">
          <Button
            onClick={() => { setIsVisible(false); onDismiss?.(); }}
            className={`w-full bg-gradient-to-r ${info.color} text-white border border-white/20 hover:border-white/40`}
          >
            <Flame className="w-4 h-4 mr-2" />
            J&apos;assume... 🔥
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// 💬 Quick tip version sombre
export const ShadowQuickTip = ({ guide = 'nyx', message, onClose }) => {
  const info = {
    nyx: { emoji: '🖤', color: 'from-purple-900 to-black', image: GUIDE_IMAGES.nyx?.confident_smirk },
    kurson: { emoji: '🔮', color: 'from-amber-900 to-black', image: GUIDE_IMAGES.kurson?.knowing_smirk }
  }[guide] || { emoji: '🖤', color: 'from-purple-900 to-black' };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex items-start gap-2 p-3 rounded-xl bg-black/80 backdrop-blur-sm border border-gray-800"
    >
      <div className={`w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br ${info.color} flex items-center justify-center text-sm flex-shrink-0`}>
        {info.image ? (
          <img src={info.image} alt={guide} className="w-full h-full object-cover" />
        ) : (
          info.emoji
        )}
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-300">{message}</p>
      </div>
      {onClose && (
        <button onClick={onClose} className="text-gray-500 hover:text-gray-400">
          <X className="w-4 h-4" />
        </button>
      )}
    </motion.div>
  );
};

export default ShadowGuide;
